/**
 * LandingPage.js
 *
 * Enhanced landing page for our Sports League Management Platform.
 * Features a modern dark blue theme, premium typography (Montserrat & Roboto),
 * compelling testimonials, clear CTAs, and improved user experience.
 */

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaFacebookF, FaTwitter, FaInstagram, FaStar, FaChevronDown, FaChevronUp } from "react-icons/fa";
import styles from "./LandingPage.module.css";
// Auth, Firebase, and Stripe imports
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { loadStripe } from "@stripe/stripe-js";
import { getFirestore, collection, addDoc, serverTimestamp } from "firebase/firestore";

function LandingPage() {
  return (
    <div className={styles.container}>
      <HeroSection />
      <FeaturesSection />
      <TestimonialsSection />
      <PricingSection />
      <NewsletterSection />
      <FAQSection />
      <Footer />
    </div>
  );
}

/* ============================== */
/*         HERO SECTION           */
/* ============================== */
function HeroSection() {
  return (
    <section className={styles["hero-section"]}>
      <div className={styles["video-background"]}>
        <video autoPlay muted loop className={styles.video} playsInline>
          <source src="/videos/hero-background.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
      <div className={styles.overlay} style={{ 
        background: "linear-gradient(rgba(10, 35, 66, 0.4), rgba(10, 35, 66, 0.5))"
      }}></div>
      <div className={styles["hero-content"]} style={{
        background: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(3px)",
        border: "1px solid rgba(255, 255, 255, 0.1)"
      }}>
        <h1 className={styles["hero-title"]} style={{ 
          textShadow: "0 2px 4px rgba(0, 0, 0, 0.9)",
          color: "white" 
        }}>Elevate Your Sports League Management</h1>
        <p className={styles["hero-subtitle"]} style={{ 
          textShadow: "0 1px 3px rgba(0, 0, 0, 0.9)",
          color: "white" 
        }}>
          The all-in-one platform that streamlines team organization, performance tracking, and league communication - trusted by coaches and administrators nationwide.
        </p>
        <div className={styles["button-group"]}>
          <Link to="/signup">
            <button className={styles["button-primary"]}>Get Started Free</button>
          </Link>
          <Link to="/login">
            <button className={styles["button-tertiary"]}>Already a Member? Log In</button>
          </Link>
        </div>
      </div>
      {/* Fixed Social Media Icons - added proper styling */}
      <div className={styles["social-media-container"]} style={{ 
        position: "absolute", 
        top: "20px", 
        right: "20px",
        zIndex: 10,
        display: "flex",
        gap: "15px"
      }}>
        <a 
          href="https://facebook.com" 
          target="_blank" 
          rel="noopener noreferrer" 
          style={{ 
            color: "#FFFFFF", 
            textDecoration: "none",
            opacity: 0.8,
            transition: "all 0.3s ease"
          }}
          aria-label="Facebook"
        >
          <FaFacebookF size={24} />
        </a>
        <a 
          href="https://twitter.com" 
          target="_blank" 
          rel="noopener noreferrer" 
          style={{ 
            color: "#FFFFFF", 
            textDecoration: "none",
            opacity: 0.8,
            transition: "all 0.3s ease"
          }}
          aria-label="Twitter"
        >
          <FaTwitter size={24} />
        </a>
        <a 
          href="https://instagram.com" 
          target="_blank" 
          rel="noopener noreferrer" 
          style={{ 
            color: "#FFFFFF", 
            textDecoration: "none",
            opacity: 0.8,
            transition: "all 0.3s ease"
          }}
          aria-label="Instagram"
        >
          <FaInstagram size={24} />
        </a>
      </div>
    </section>
  );
}

/* ============================== */
/*        FEATURES SECTION        */
/* ============================== */
function FeaturesSection() {
  return (
    <section className={styles["features-section"]}>
      <h2 className={styles["section-title"]}>Powerful Features for Every League</h2>
      <p style={{
        fontSize: "1.2rem",
        maxWidth: "700px",
        margin: "0 auto 40px",
        color: "var(--light-color)",
        opacity: "0.8"
      }}>
        Designed by sports administrators for sports administrators
      </p>
      <div className={styles["features-grid"]}>
        <FeatureCard 
          title="Intelligent Team Management" 
          description="Create teams, manage rosters, and track player availability all in one place." 
          icon="🏆" 
        />
        <FeatureCard 
          title="Comprehensive Analytics" 
          description="Track player and team stats with easy-to-understand visualizations and reports." 
          icon="📊" 
        />
        <FeatureCard 
          title="Smart Scheduling" 
          description="Automatically generate conflict-free schedules with weather alerts and field availability." 
          icon="📅" 
        />
        <FeatureCard 
          title="Unified Communication Hub" 
          description="Keep coaches, players, and parents informed with targeted announcements, alerts, and private messaging." 
          icon="💬" 
        />
      </div>
      <div style={{marginTop: "50px", textAlign: "center"}}>
        <Link to="/features">
          <button style={{
            backgroundColor: "#1976D2", 
            color: "#FFFFFF",
            padding: "15px 30px",
            borderRadius: "8px",
            border: "none",
            fontSize: "1.1rem",
            fontWeight: "600",
            cursor: "pointer",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            transition: "all 0.3s ease"
          }}>
            Explore All Features
          </button>
        </Link>
      </div>
    </section>
  );
}

function FeatureCard({ title, description, icon }) {
  return (
    <div className={styles["feature-card"]}>
      <div className={styles["feature-icon"]}>{icon}</div>
      <h3 className={styles["feature-title"]}>{title}</h3>
      <p className={styles["feature-description"]}>{description}</p>
    </div>
  );
}

/* ============================== */
/*      TESTIMONIALS SECTION      */
/* ============================== */
function TestimonialsSection() {
  const testimonials = [
    {
      name: "Alex Rodriguez",
      role: "League Commissioner",
      organization: "Metro Youth Baseball",
      feedback: "This platform has completely transformed how we manage our 24-team league. The automated scheduling alone saved us 15+ hours of work each season. The interface is intuitive and the support team is outstanding.",
      rating: 5
    },
    {
      name: "Jamie Lee",
      role: "Athletic Director",
      organization: "Westside High School",
      feedback: "Professional, clean, and effective. Our coaches love the real-time stats tracking, and parents appreciate the instant game updates. I couldn't imagine running our athletics program without this tool.",
      rating: 5
    },
    {
      name: "Sam Keller",
      role: "Tournament Director",
      organization: "Regional Soccer Cup",
      feedback: "A must-have for every sports organization! We manage 80+ teams and 200+ games during our tournament weekends, and this platform handles it flawlessly. It simplifies scheduling, analytics, and communication effortlessly.",
      rating: 5
    }
  ];

  return (
    <section className={styles["testimonials-section"]}>
      <h2 className={styles["section-title"]}>Trusted by League Leaders</h2>
      <div className={styles["testimonials-grid"]}>
        {testimonials.map((testimonial, index) => (
          <TestimonialCard 
            key={index}
            name={testimonial.name} 
            role={testimonial.role}
            organization={testimonial.organization}
            feedback={testimonial.feedback} 
            rating={testimonial.rating}
          />
        ))}
      </div>
    </section>
  );
}

function TestimonialCard({ name, role, organization, feedback, rating }) {
  return (
    <div className={styles["testimonial-card"]}>
      <div style={{ marginBottom: "15px" }}>
        {[...Array(rating)].map((_, i) => (
          <FaStar key={i} style={{ color: "#FFD700", marginRight: "5px" }} />
        ))}
      </div>
      <p className={styles["testimonial-feedback"]}>"{feedback}"</p>
      <div style={{marginTop: "15px"}}>
        <p className={styles["testimonial-name"]}><strong>{name}</strong></p>
        <p style={{fontSize: "0.9rem", color: "#1976D2"}}>{role}, {organization}</p>
      </div>
    </div>
  );
}

/* ============================== */
/*         PRICING SECTION        */
/* ============================== */
function PricingSection() {
  const plans = [
    {
      name: "Free",
      price: "$0",
      period: "forever",
      features: [
        "Up to 3 teams",
        "Basic scheduling",
        "Team roster management",
        "Game results tracking",
        "Basic communication tools",
        "Email support"
      ],
      highlight: false
    },
    {
      name: "Pro",
      price: "$10",
      period: "per month",
      features: [
        "Unlimited teams",
        "Advanced scheduling & conflicts management",
        "Comprehensive statistics & analytics",
        "Custom branding options",
        "Payment collection tools",
        "Priority email & chat support"
      ],
      highlight: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      features: [
        "Everything in Pro plus:",
        "Multi-league management",
        "API access",
        "Custom development options",
        "Dedicated account manager",
        "24/7 premium support"
      ],
      highlight: false
    }
  ];

  return (
    <section className={styles["pricing-section"]}>
      <h2 className={styles["section-title"]}>Simple, Transparent Pricing</h2>
      <p style={{
        fontSize: "1.2rem",
        maxWidth: "700px",
        margin: "0 auto 40px",
        color: "var(--light-color)",
        opacity: "0.8"
      }}>
        Scale with your league's needs. No hidden fees.
      </p>
      <div className={styles["pricing-grid"]}>
        {plans.map((plan, index) => (
          <PricingCard
            key={index}
            plan={plan.name}
            price={plan.price}
            period={plan.period}
            features={plan.features}
            highlight={plan.highlight}
          />
        ))}
      </div>
    </section>
  );
}

function PricingCard({ plan, price, period, features, highlight }) {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const functions = getFunctions();
  const [isHovered, setIsHovered] = useState(false);

  const handleSelectPlan = async () => {
    if (!user) {
      sessionStorage.setItem("selectedPlan", plan);
      navigate("/login?redirect=pricing");
      return;
    }
    
    if (plan === "Free") {
      navigate("/dashboard");
      return;
    }
    
    if (plan === "Enterprise") {
      navigate("/support");
      return;
    }
    
    try {
      const createStripeSession = httpsCallable(functions, "createStripeSession");
      const result = await createStripeSession({ 
        plan: plan, 
        userId: user.uid,
        returnUrl: window.location.origin + "/dashboard"
      });
      const sessionId = result.data.id;
      const stripe = await loadStripe("pk_test_51Qzl4xB2xpuvoM49SPm8MOswuGnaNaQ2u8w8S6bKEukqjwH6USFNE070jjnCZq6W5QfnvHW7qcU0RLV0wkrbo1B800d4pkrwcl");
      if (!stripe) {
        alert("Failed to load payment processor. Please try again.");
        return;
      }
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        console.error("Stripe redirection error:", error);
        alert("Payment processing failed. Please try again.");
      }
    } catch (error) {
      console.error("Error processing payment:", error);
      alert("Unable to process your request. Please try again later.");
    }
  };

  const cardClass = highlight 
    ? `${styles["pricing-card"]} ${styles["pricing-card-highlight"]}` 
    : styles["pricing-card"];

  // Custom button style for pricing buttons
  const buttonStyle = {
    width: '100%',
    marginTop: '25px',
    padding: '15px 20px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    position: 'relative',
    zIndex: 10,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    transform: isHovered ? 'translateY(-3px)' : 'none',
    ...(plan === "Pro" ? {
      backgroundColor: '#FFD700', // Gold
      color: '#0A2342',           // Dark blue
      border: '2px solid #FFD700'
    } : {
      backgroundColor: '#1976D2', // Blue
      color: 'white',
      border: '2px solid #1976D2'
    })
  };

  return (
    <div 
      className={cardClass}
      style={{ position: 'relative' }}
    >
      {highlight && <div style={{
        position: "absolute",
        top: "-12px",
        left: "50%",
        transform: "translateX(-50%)",
        backgroundColor: "#FFD700",
        color: "#0A2342",
        padding: "5px 15px",
        borderRadius: "20px",
        fontWeight: "bold",
        fontSize: "0.9rem",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        zIndex: 1
      }}>Most Popular</div>}

      <h3 className={styles["pricing-plan"]}>{plan}</h3>
      
      <div style={{ margin: '15px 0' }}>
        <span className={styles["pricing-price"]}>{price}</span>
        {period && <span style={{ fontSize: "1rem", opacity: 0.7 }}>/{period}</span>}
      </div>
      
      <ul className={styles["pricing-features"]}>
        {features.map((feature, index) => (
          <li key={index} className={styles["pricing-feature"]}>
            {feature}
          </li>
        ))}
      </ul>
      
      <button 
        onClick={handleSelectPlan}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={buttonStyle}
      >
        {plan === "Free" ? "Get Started" : plan === "Enterprise" ? "Contact Sales" : "Select Plan"}
      </button>
    </div>
  );
}

/* ============================== */
/*       NEWSLETTER SECTION       */
/* ============================== */
function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const db = getFirestore();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, "newsletterSubscribers"), {
        email,
        subscribedAt: serverTimestamp(),
        source: "landing_page"
      });
      alert("You've been successfully subscribed!");
      setEmail("");
    } catch (error) {
      console.error("Error subscribing:", error);
      alert("Subscription failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className={styles["newsletter-section"]}>
      <h2 className={styles["section-title"]}>Join Our Community</h2>
      <p className={styles["newsletter-text"]}>
        Subscribe to receive exclusive tips, feature updates, and sports management insights.
      </p>
      <form className={styles["newsletter-form"]} onSubmit={handleSubscribe}>
        <input
          name="email"
          type="email"
          placeholder="Your email address"
          className={styles["newsletter-input"]}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <button 
          type="submit" 
          className={styles["button-primary"]}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Subscribing..." : "Subscribe"}
        </button>
      </form>
      <p style={{fontSize: "0.8rem", opacity: "0.7", marginTop: "10px"}}>
        We respect your privacy. Unsubscribe at any time.
      </p>
    </section>
  );
}

/* ============================== */
/*           FAQ SECTION          */
/* ============================== */
function FAQSection() {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqs = [
    {
      question: "How quickly can I get started with SportsHive?",
      answer:
        "Signing up is fast and easy. Create your account, set up your league, and start managing teams within minutes using our intuitive onboarding process."
    },
    {
      question: "Do you offer a free trial for the Pro plan?",
      answer:
        "Yes! For a free trial of our Pro plan, please contact our support team. They will help you access all the advanced features so you can experience the full power of SportsHive without any upfront commitment."
    },
    {
      question: "What subscription plans are available?",
      answer:
        "We offer three tiers: Free (ideal for small leagues with limited teams), Pro ($10/month for unlimited teams and advanced features), and Enterprise (custom pricing for multi-league organizations with premium support)."
    },
    {
      question: "How can I upgrade or change my subscription?",
      answer:
        "You can easily upgrade, downgrade, or cancel your subscription through your account settings. Changes take effect at the end of your current billing cycle. For Enterprise-level needs, please contact our sales team."
    },
    {
      question: "Is my data secure with SportsHive?",
      answer:
        "Absolutely. We use industry-standard encryption and robust security measures to protect your data. We comply with all relevant privacy regulations to ensure your information remains safe."
    },
    {
      question: "How do I get support if I need help?",
      answer:
        "Our dedicated support team is here to help. Free users have access to our comprehensive knowledge base and email support, while Pro and Enterprise customers enjoy priority support via email and chat. For any issues, please contact our support team directly."
    }
  ];

  return (
    <section className={styles["faq-section"]}>
      <h2 className={styles["section-title"]}>Frequently Asked Questions</h2>
      <div className={styles["faq-list"]}>
        {faqs.map((faq, index) => (
          <FAQItem key={index} question={faq.question} answer={faq.answer} isActive={activeIndex === index} onClick={() => toggleFAQ(index)} />
        ))}
      </div>
      <div style={{ marginTop: "40px", textAlign: "center" }}>
        <p style={{ marginBottom: "15px" }}>Still have questions?</p>
        <Link to="/support">
          {/* Fixed Contact Our Team button with solid styling */}
          <button style={{
            backgroundColor: "#1976D2", 
            color: "#FFFFFF",
            padding: "15px 30px",
            borderRadius: "8px",
            border: "none",
            fontSize: "1.1rem",
            fontWeight: "600",
            cursor: "pointer",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            transition: "all 0.3s ease"
          }}>
            Contact Our Support
          </button>
        </Link>
      </div>
    </section>
  );
}

function FAQItem({ question, answer, isActive, onClick }) {
  const itemClass = isActive ? `${styles["faq-item"]} ${styles["faq-open"]}` : styles["faq-item"];
  return (
    <div className={itemClass} onClick={onClick}>
      <div style={{
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center"
      }}>
        <h4 className={styles["faq-question"]}>{question}</h4>
        <span className={styles["faq-toggle"]}>
          {isActive ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      </div>
      {isActive && <p className={styles["faq-answer"]}>{answer}</p>}
    </div>
  );
}

/* ============================== */
/*          FOOTER SECTION        */
/* ============================== */
function Footer() {
  return (
    <footer className={styles.footer}>
      <div className={styles["footer-content"]}>
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          marginBottom: "30px",
          textAlign: "left"
        }}>
          <div style={{ margin: "0 20px 20px 0", minWidth: "160px" }}>
            <h4 style={{ marginBottom: "15px", color: "#FFD700" }}>Product</h4>
            <ul style={{ listStyle: "none", padding: 0 }}>
              <li style={{ marginBottom: "8px" }}><Link to="/features" style={{ color: "#FFFFFF", textDecoration: "none" }}>Features</Link></li>
              <li style={{ marginBottom: "8px" }}><Link to="/pricing" style={{ color: "#FFFFFF", textDecoration: "none" }}>Pricing</Link></li>
              <li style={{ marginBottom: "8px" }}><Link to="/integrations" style={{ color: "#FFFFFF", textDecoration: "none" }}>Integrations</Link></li>
              <li style={{ marginBottom: "8px" }}><Link to="/updates" style={{ color: "#FFFFFF", textDecoration: "none" }}>What's New</Link></li>
            </ul>
          </div>
          <div style={{ margin: "0 20px 20px 0", minWidth: "160px" }}>
            <h4 style={{ marginBottom: "15px", color: "#FFD700" }}>Resources</h4>
            <ul style={{ listStyle: "none", padding: 0 }}>
              <li style={{ marginBottom: "8px" }}><Link to="/blog" style={{ color: "#FFFFFF", textDecoration: "none" }}>Blog</Link></li>
              <li style={{ marginBottom: "8px" }}><Link to="/guides" style={{ color: "#FFFFFF", textDecoration: "none" }}>Guides</Link></li>
              <li style={{ marginBottom: "8px" }}><Link to="/webinars" style={{ color: "#FFFFFF", textDecoration: "none" }}>Webinars</Link></li>
              <li style={{ marginBottom: "8px" }}><Link to="/help" style={{ color: "#FFFFFF", textDecoration: "none" }}>Help Center</Link></li>
            </ul>
          </div>
          <div style={{ margin: "0 20px 20px 0", minWidth: "160px" }}>
            <h4 style={{ marginBottom: "15px", color: "#FFD700" }}>Company</h4>
            <ul style={{ listStyle: "none", padding: 0 }}>
              <li style={{ marginBottom: "8px" }}><Link to="/about" style={{ color: "#FFFFFF", textDecoration: "none" }}>About Us</Link></li>
              <li style={{ marginBottom: "8px" }}><Link to="/careers" style={{ color: "#FFFFFF", textDecoration: "none" }}>Careers</Link></li>
              <li style={{ marginBottom: "8px" }}><Link to="/support" style={{ color: "#FFFFFF", textDecoration: "none" }}>Contact</Link></li>
              <li style={{ marginBottom: "8px" }}><Link to="/partners" style={{ color: "#FFFFFF", textDecoration: "none" }}>Partners</Link></li>
            </ul>
          </div>
          <div style={{ margin: "0 20px 20px 0", minWidth: "160px" }}>
            <h4 style={{ marginBottom: "15px", color: "#FFD700" }}>Legal</h4>
            <ul style={{ listStyle: "none", padding: 0 }}>
              <li style={{ marginBottom: "8px" }}><Link to="/terms" style={{ color: "#FFFFFF", textDecoration: "none" }}>Terms of Service</Link></li>
              <li style={{ marginBottom: "8px" }}><Link to="/privacy" style={{ color: "#FFFFFF", textDecoration: "none" }}>Privacy Policy</Link></li>
              <li style={{ marginBottom: "8px" }}><Link to="/security" style={{ color: "#FFFFFF", textDecoration: "none" }}>Security</Link></li>
              <li style={{ marginBottom: "8px" }}><Link to="/gdpr" style={{ color: "#FFFFFF", textDecoration: "none" }}>GDPR Compliance</Link></li>
            </ul>
          </div>
        </div>
        <div style={{
          borderTop: "1px solid rgba(255,255,255,0.1)",
          paddingTop: "20px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center"
        }}>
          {/* Fixed Social Media Icons in footer */}
          <div style={{
            display: "flex", 
            gap: "20px",
            marginBottom: "15px"
          }}>
            <a 
              href="https://facebook.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ 
                color: "#FFFFFF", 
                textDecoration: "none",
                opacity: 0.8,
                transition: "all 0.3s ease"
              }}
              aria-label="Facebook"
            >
              <FaFacebookF size={20} />
            </a>
            <a 
              href="https://twitter.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ 
                color: "#FFFFFF", 
                textDecoration: "none",
                opacity: 0.8,
                transition: "all 0.3s ease"
              }}
              aria-label="Twitter"
            >
              <FaTwitter size={20} />
            </a>
            <a 
              href="https://instagram.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ 
                color: "#FFFFFF", 
                textDecoration: "none",
                opacity: 0.8,
                transition: "all 0.3s ease"
              }}
              aria-label="Instagram"
            >
              <FaInstagram size={20} />
            </a>
          </div>
          <p className={styles["footer-text"]}>
            © {new Date().getFullYear()} Sports League Management. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default LandingPage;